import { motion } from "framer-motion";
import { Trophy, Wrench, Lightning, MapPin, Phone, EnvelopeSimple, Clock } from "@phosphor-icons/react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { resolveAssetUrl } from "@/lib/api";

const PILL_ICONS = [Wrench, Lightning, Trophy];

export default function About() {
  const content = useSiteContent();
  if (!content) return <div className="pt-32 text-white/50 text-center">Φόρτωση...</div>;
  const { about, contact } = content;

  return (
    <div data-testid="about-page" className="pt-40 md:pt-52 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="text-overline mb-4">{about.overline}</p>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.9]">
              {about.title_line1}<br/> {about.title_line2}<br/>
              <span className="text-[#E50000]">{about.title_accent}</span>
            </h1>
          </div>
          <div className="lg:col-span-5 text-white/65 leading-relaxed">
            <p>{about.story_p1}</p>
            <p className="mt-4">{about.story_p2}</p>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border border-white/10">
          {(about.stats || []).map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`p-8 md:p-10 ${i < 3 ? "md:border-r border-white/10" : ""} ${i < 2 ? "border-b md:border-b-0 border-white/10" : ""}`}
            >
              <p className="font-display font-black text-5xl text-[#E50000]">{s.value}</p>
              <p className="mt-3 text-xs uppercase tracking-widest text-white/60 font-mono">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-24 grid lg:grid-cols-2 gap-px bg-white/10">
          <div className="bg-[#0A0A0A] p-10 md:p-14">
            <p className="text-overline mb-4">{about.story_overline}</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">{about.story_title}</h2>
            <p className="mt-6 text-white/65 leading-relaxed">{about.story_full}</p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {(about.story_pills || []).map((label, i) => {
                const Icon = PILL_ICONS[i] || Wrench;
                return (
                  <div key={i} className="border border-white/10 p-4">
                    <Icon size={20} weight="duotone" className="text-[#E50000]" />
                    <p className="mt-3 text-xs font-bold uppercase tracking-wider text-white/80">{label}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-[#0A0A0A]">
            <img src={resolveAssetUrl(about.story_image_url)} alt="Εργαστήριο" className="w-full h-full object-cover min-h-[400px]" />
          </div>
        </div>

        <div className="mt-24">
          <p className="text-overline mb-4">{about.team_overline}</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">{about.team_title}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {(about.team || []).map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-white/10 bg-[#141414] group hover:border-[#E50000]/60 transition-colors"
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#0A0A0A]">
                  <img src={resolveAssetUrl(t.img)} alt={t.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                </div>
                <div className="p-6">
                  <p className="font-display font-black uppercase text-xl">{t.name}</p>
                  <p className="text-overline mt-1">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div data-testid="contact-section" className="mt-24 grid lg:grid-cols-12 gap-10 border-t border-white/10 pt-16">
          <div className="lg:col-span-5">
            <p className="text-overline mb-4">{contact.overline}</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">{contact.title}</h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10">
            <Info icon={MapPin} label="Showroom" value={contact.address} />
            <Info icon={Phone} label="Τηλέφωνο" value={contact.phone} />
            <Info icon={EnvelopeSimple} label="Email" value={contact.email} />
            <Info icon={Clock} label="Ωράριο" value={contact.hours} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#0A0A0A] p-8">
      <Icon size={22} weight="duotone" className="text-[#E50000]" />
      <p className="mt-4 text-overline">{label}</p>
      <p className="mt-1 font-display text-lg text-white">{value}</p>
    </div>
  );
}
